import { cva, type VariantProps } from 'class-variance-authority';

import { textVariants } from '@/components/ui/text';
import { cn } from '@/utils/helpers/cn';

import type { ComponentPropsWithoutRef } from 'react';

/**
 * Botão de ação. Não existe `asChild`: o projeto não tem Radix instalado, e um
 * Slot próprio custaria mais do que resolve. Um `<Link>` que precisa parecer
 * botão consome `buttonVariants` direto:
 *
 * ```tsx
 * <Link to="/entrar" className={buttonVariants({ variant: 'secondary' })}>Entrar</Link>
 * ```
 *
 * A tipografia vem de `textVariants`, e não de `text-*`/`font-*` escritos aqui:
 * é o mesmo motivo pelo qual `<Text>` existe — se a escala mudar, ela muda num
 * arquivo só e o botão acompanha (docs/03-componentes-e-ui.md).
 */
export const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 rounded-lg transition-colors',
    // Sem `pointer-events-none` o botão desabilitado ainda dispara `:active` e
    // o usuário lê o feedback visual como "clicou e nada aconteceu".
    'disabled:pointer-events-none disabled:opacity-50',
  ),
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground active:bg-primary-hover',
        secondary: 'border-input bg-surface text-foreground border active:bg-muted',
        ghost: 'text-foreground active:bg-muted',
        // Variante do fundo escuro (`--shell`): a `ghost` normal usa
        // `text-foreground`, que é o navy da marca — sobre a tela escura isso dá
        // 1,07:1 e o botão fica literalmente invisível. Medido em /welcome.
        ghostOnShell: 'text-shell-foreground active:bg-shell-border',
        destructive: 'bg-destructive text-destructive-foreground active:bg-destructive/90',
        // Gatilho destrutivo **em lista** ("Apagar", "Bloquear", "Remover"): o
        // vermelho sólido fica reservado ao botão que confirma dentro da sheet.
        // Uma coluna de botões sólidos vermelhos grita perigo em cada linha e
        // deixa o toque errado a um polegar (auditoria de UX, §1.9). Existe para
        // que o pai nunca precise de `className="text-destructive"` — override
        // de variante pelo pai, proibido em docs/03.
        ghostDestructive: 'text-destructive active:bg-destructive-subtle',
        // Continua com alvo de toque cheio: parecer link não dispensa os 44px.
        link: 'text-primary underline underline-offset-4',
      },
      size: {
        // Nenhum tamanho fica abaixo de 44px de altura — é o mínimo das
        // guidelines de plataforma e a régua de docs/03. `min-h` e não `h`
        // porque rótulo longo quebra em duas linhas em tela estreita, e com
        // altura fixa o texto vazaria da caixa.
        md: cn('min-h-11 px-4', textVariants({ variant: 'subtitle' })),
        lg: cn('min-h-12 px-5', textVariants({ variant: 'title' })),
        icon: 'size-11 p-0',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      // `lg` é o default porque a maioria dos botões do app é o CTA de largura
      // cheia no rodapé da tela — o caso raro é que deve pedir `size="md"`.
      variant: 'primary',
      size: 'lg',
      fullWidth: false,
    },
  },
);

type ButtonProps = ComponentPropsWithoutRef<'button'> & VariantProps<typeof buttonVariants>;

export function Button({ variant, size, fullWidth, className, type, ...props }: ButtonProps) {
  return (
    <button
      // O default do HTML é `submit`: um botão auxiliar esquecido dentro de um
      // `<form>` envia o formulário sem ninguém pedir. Aqui o default é
      // `button`, e quem submete declara `type="submit"`.
      //
      // A regra abaixo exige literal no JSX, o que impediria repassar o `type`
      // do consumidor — e o default que ela protege já está garantido aqui.
      // eslint-disable-next-line react/button-has-type
      type={type ?? 'button'}
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      {...props}
    />
  );
}
